<template>
  <div class="entra-portfolio">
    <EntraPageHeader
      title="Entra ID Portfolio"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showRefresh="false"
      :showExport="false"
    />

    <div class="page-body">
      <div class="kpi-grid">
        <MetricCard label="Managed Tenants" :value="summary.managedTenants" :state="summaryState" />
        <MetricCard label="Collection Errors" :value="summary.tenantsWithErrors" :state="summaryState" />
        <MetricCard label="Total Member Users" :value="summary.memberUsers" :state="summaryState" />
        <MetricCard label="Total Guest Users" :value="summary.guestUsers" :state="summaryState" />
        <MetricCard label="Total Licensed Users" :value="summary.licensedUsers" :state="summaryState" />
        <MetricCard label="Critical Findings" :value="summary.openCriticalFindings" :state="summaryState" />
        <MetricCard label="High Findings" :value="summary.openHighFindings" :state="summaryState" />
        <MetricCard label="Disabled Licensed" :value="summary.disabledLicensedUsers" :state="summaryState" />
      </div>

      <div class="filters-bar">
        <div class="filter-group">
          <label class="filter-label">Region:</label>
          <select v-model="filters.region" class="filter-select" @change="applyFilters">
            <option value="">All Regions</option>
            <option value="UK">UK</option>
            <option value="CA">CA</option>
            <option value="US">US</option>
            <option value="AS">AS</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Status:</label>
          <select v-model="filters.status" class="filter-select" @change="applyFilters">
            <option value="">All Statuses</option>
            <option value="healthy">Healthy</option>
            <option value="warning">Warning</option>
            <option value="failed">Failed</option>
            <option value="consent-required">Consent Required</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Min Severity:</label>
          <select v-model="filters.minSeverity" class="filter-select" @change="applyFilters">
            <option value="">Any</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Sort:</label>
            <select v-model="filters.sort" class="filter-select" @change="applyFilters">
              <option value="-criticalFindings">Most Critical Findings</option>
              <option value="-highFindings">Most High Findings</option>
              <option value="-users">Most Users</option>
              <option value="displayName">Name (A-Z)</option>
              <option value="-displayName">Name (Z-A)</option>
              <option value="lastCollection">Oldest Sync</option>
              <option value="-lastCollection">Newest Sync</option>
            </select>
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else-if="tenantTableState === 'loading'" class="loading-state">
        <span>Loading tenants...</span>
      </div>

      <div v-else-if="tenantTableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No tenants found" :description="'No managed tenants match the current filters.'" />
      </div>

      <div v-else class="tenant-table-wrapper">
        <table class="tenant-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Region</th>
              <th>Users</th>
              <th>Licensed</th>
              <th>Guests</th>
              <th>Critical</th>
              <th>High</th>
              <th>Last Collection</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tenant in tenants" :key="tenant.tenantId" class="tenant-row" @click="goToTenant(tenant.tenantId)">
              <td class="tenant-cell">
                <div class="tenant-name">{{ tenant.displayName }}</div>
                <div class="tenant-domain">{{ tenant.primaryDomain }}</div>
              </td>
              <td><span class="region-badge">{{ tenant.regionCode }}</span></td>
              <td>{{ formatCount(tenant.users) }}</td>
              <td>{{ formatCount(tenant.licensedUsers) }}</td>
              <td>{{ formatCount(tenant.guestUsers) }}</td>
              <td :class="{ 'critical-cell': tenant.criticalFindings > 0 }">{{ formatCount(tenant.criticalFindings) }}</td>
              <td :class="{ 'high-cell': tenant.highFindings > 0 }">{{ formatCount(tenant.highFindings) }}</td>
              <td class="date-cell">{{ formatRelativeTime(tenant.lastSuccessfulCollectionAt) }}</td>
              <td>
                <span class="status-chip" :class="'status-' + tenant.status">{{ statusLabel(tenant.status) }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>

      <div class="charts-grid">
        <div class="chart-card placeholder-chart">
          <h3>Findings by Severity</h3>
          <p class="chart-placeholder-text">Chart will render here when data is available</p>
        </div>
        <div class="chart-card placeholder-chart">
          <h3>Potential Savings by Tenant</h3>
          <p class="chart-placeholder-text">Chart will render here when data is available</p>
        </div>
        <div class="chart-card placeholder-chart">
          <h3>MFA Coverage Distribution</h3>
          <p class="chart-placeholder-text">Chart will render here when data is available</p>
        </div>
        <div class="chart-card placeholder-chart">
          <h3>Licence Consumption Trend</h3>
          <p class="chart-placeholder-text">Chart will render here when data is available</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import { getEntraPortfolioSummary, getEntraPortfolioTenants } from '@/services/entra-api.js';
import { formatCurrency, formatPercentage, formatCount, getCollectionStatusLabel } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';

var SORT_TRANSLATION = {
  '-criticalFindings': '-criticalFindings',
  '-highFindings': '-highFindings',
  '-users': '-users',
  'displayName': 'displayName',
  '-displayName': '-displayName',
  'lastCollection': 'lastCollection',
  '-lastCollection': '-lastCollection'
};

export default {
  name: 'EntraPortfolio',
  components: { EntraPageHeader, MetricCard, EmptyState },
  data: function() {
    return {
      summary: {
        tenantCount: 0, tenantsWithErrors: 0, totalMemberUsers: 0, totalGuestUsers: 0,
        totalLicensedUsers: 0, estimatedMonthlySpend: 0, estimatedMonthlySaving: 0,
        criticalFindings: 0, highFindings: 0, avgMfaCoverage: 0, avgHealthScore: null,
        credentialsExpiring: 0
      },
      summaryState: 'loading',
      tenants: [],
      tenantTableState: 'loading',
      filters: { region: '', status: '', minSeverity: '', sort: '-estimatedMonthlySaving' },
      page: 1,
      totalPages: 1,
      error: ''
    };
  },
  methods: {
    formatCurrency: formatCurrency,
    formatPercentage: formatPercentage,
    formatCount: formatCount,
    statusLabel: getCollectionStatusLabel,
    mfaColor: function(value) {
      if (value === null || value === undefined) return '#94a3b8';
      if (value >= 90) return '#16a34a';
      if (value >= 70) return '#ca8a04';
      return '#dc2626';
    },
    formatRelativeTime: function(isoString) {
      if (!isoString) return '--';
      return formatDate(isoString, { mode: 'relative' });
    },
    loadData: function() {
      var self = this;
      this.error = '';

      getEntraPortfolioSummary().then(function(data) {
        self.summary = data;
        self.summaryState = 'loaded';
      }).catch(function(err) {
        console.error('Failed to load portfolio summary:', err);
        self.summaryState = 'error';
      });

      this.loadTenants();
    },
    loadTenants: function() {
      var self = this;
      this.tenantTableState = 'loading';

      var params = {
        page: this.page,
        pageSize: 50,
        sort: SORT_TRANSLATION[this.filters.sort] || 'displayName'
      };
      if (this.filters.region) params.region = this.filters.region;
      if (this.filters.status) params.status = this.filters.status;
      if (this.filters.minSeverity) params.severity = this.filters.minSeverity;

      getEntraPortfolioTenants(params).then(function(data) {
        self.tenants = data.items || [];
        self.totalPages = Math.ceil((data.total || 0) / 50);
        self.tenantTableState = self.tenants.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) {
        console.error('Failed to load tenants:', err);
        self.error = err.message || 'Failed to load tenant data.';
        self.tenantTableState = 'error';
      });
    },
    applyFilters: function() {
      this.page = 1;
      this.loadTenants();
    },
    goToPage: function(newPage) {
      this.page = newPage;
      this.loadTenants();
    },
    goToTenant: function(tenantId) {
      this.$router.push('/entra/tenants/' + encodeURIComponent(tenantId) + '/overview');
    }
  },
  mounted: function() {
    this.loadData();
  }
};
</script>

<style scoped>
.entra-portfolio {
  min-height: 100%;
}

.page-body {
  padding: 24px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.filters-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
}

.filter-select {
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.8125rem;
  color: #334155;
  background: #ffffff;
  font-family: 'Inter', sans-serif;
  outline: none;
}
.filter-select:focus { border-color: #3b82f6; }

.error-message {
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.875rem;
}

.loading-state {
  padding: 48px;
  text-align: center;
  color: #94a3b8;
}

.empty-wrapper {
  padding: 24px 0;
}

.tenant-table-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
}

.tenant-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.tenant-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}

.tenant-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.tenant-row {
  cursor: pointer;
  transition: background 0.15s;
}
.tenant-row:hover { background: #f8fafc; }

.tenant-name {
  font-weight: 600;
  color: #1e293b;
}
.tenant-domain {
  font-size: 0.6875rem;
  color: #94a3b8;
}

.region-badge {
  background: #eff6ff;
  color: #2563eb;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.6875rem;
}

.saving-cell { color: #16a34a; font-weight: 500; }

.date-cell { font-size: 0.75rem; color: #64748b; white-space: nowrap; }

.critical-cell { color: #dc2626; font-weight: 700; }
.high-cell { color: #ea580c; font-weight: 600; }

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.status-healthy { background: #dcfce7; color: #166534; }
.status-warning { background: #fef3c7; color: #92400e; }
.status-failed { background: #fee2e2; color: #991b1b; }
.status-consent-required { background: #f1f5f9; color: #475569; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid #e2e8f0;
}

.page-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #475569;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.page-info { font-size: 0.8125rem; color: #64748b; }

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
}

.chart-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
}

.chart-card h3 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin: 0 0 8px 0;
}

.chart-placeholder-text {
  font-size: 0.75rem;
  color: #94a3b8;
  font-style: italic;
}
</style>
